// A growable interleaved vertex buffer.
//
// Layout, 28 bytes per vertex:
//   0  vec2  position   (batch-local units)
//   8  vec2  uv         (atlas coordinates, or -1..1 for analytic circles)
//  16  ubyte4 colour    (RGBA, normalised)
//  20  vec2  extra      (mode, mode-specific parameter)

export const FLOATS_PER_VERTEX = 7;
export const VERTEX_STRIDE = FLOATS_PER_VERTEX * 4;

export class VertexBuffer {
  constructor(capacity = 256) {
    this.capacity = capacity;
    this.length = 0; // vertices written
    this._allocate(capacity);
  }

  _allocate(capacity) {
    const bytes = new ArrayBuffer(capacity * VERTEX_STRIDE);
    const f32 = new Float32Array(bytes);
    const u8 = new Uint8Array(bytes);

    if (this.f32)
      f32.set(this.f32.subarray(0, this.length * FLOATS_PER_VERTEX));

    this.bytes = bytes;
    this.f32 = f32;
    this.u8 = u8;
    this.capacity = capacity;
  }

  reset() {
    this.length = 0;
  }

  ensure(extra) {
    const needed = this.length + extra;
    if (needed <= this.capacity) return;
    let capacity = this.capacity || 256;
    while (capacity < needed) capacity *= 2;
    this._allocate(capacity);
  }

  // `colour` is 0xRRGGBBAA. The bytes are written individually rather than as one
  // word because the GPU reads them in memory order, which a word write would
  // make depend on the machine's endianness.
  push(x, y, u, v, colour, mode, param) {
    const i = this.length * FLOATS_PER_VERTEX;
    const f32 = this.f32;
    f32[i] = x;
    f32[i + 1] = y;
    f32[i + 2] = u;
    f32[i + 3] = v;
    f32[i + 5] = mode;
    f32[i + 6] = param;

    const b = this.length * VERTEX_STRIDE + 16;
    const u8 = this.u8;
    u8[b] = (colour >>> 24) & 0xff;
    u8[b + 1] = (colour >>> 16) & 0xff;
    u8[b + 2] = (colour >>> 8) & 0xff;
    u8[b + 3] = colour & 0xff;

    this.length++;
  }

  // The written region, for upload.
  view() {
    return this.f32.subarray(0, this.length * FLOATS_PER_VERTEX);
  }

  viewRange(fromVertex, vertexCount) {
    return this.f32.subarray(
      fromVertex * FLOATS_PER_VERTEX,
      (fromVertex + vertexCount) * FLOATS_PER_VERTEX
    );
  }
}
